/* eslint-disable @typescript-eslint/no-explicit-any */

import { FC, useState } from "react";
import { useStores } from "@/app/csr-src/models";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";

import to from "await-to-js";
import { getPayResult, redirectPayResult, repayOrder } from "@/app/csr-src/apis/checkout/checkout.api";
import { PAY_STATUS, PAY_TYPE, RESULT_STR } from "@/app/csr-src/apis/checkout/constants/constants";

import ReCard from "./repayButton/card";
import Default from "./repayButton/default";
import Repaypal from "./repayButton/paypal";
import KlarnaPay from "./repayButton/klarna";
import GooglePayButton from "./repayButton/google";
import WechatPayButton from "./repayButton/wechat";
import ReApplePayButton from "./repayButton/apple";

import Styles from "./repayButtons.module.scss";
import UKToast from "@/common-components-src/components/UKToast/index";
import { EXPOSURE_RECORD, sendClickTrack } from "@/tracks/26172";
import { Button, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import PayRiskModal from "./payRiskModal";
interface RepayButtonProps {
  onSubmitError: (data: any) => void; // 提单失败后的回调
  onFinish: () => void; // 支付完成后的回调
  onFresh: () => void; // 刷新订单状态
  onClose: () => void; // 关闭弹窗
}

const REPAY_RESULT_CODE = {
  SUCCESS: "200",
  RISK_ERROR: "10003",
}
const CANCEL_URL = "/profile/my-orders";

const RepayButton: FC<RepayButtonProps> = observer((props) => {
  const { checkoutStore, orderStore } = useStores();
  const { t } = useTranslation();
  const { setPayResult } = checkoutStore;

  const [riskErrorOpen, setRiskErrorOpen] = useState(false);
  const [commonError, setCommonError] = useState("");

  /**
   *
   * 二次支付逻辑处理
   * 失败统一处理；成功使用各支付方式的回调方法
   *
   */
  const handleSubmitOrder = async () => {
    sendClickTrack.Cashier_Pop_Pay({
      orderId: checkoutStore.repayOrderId || "-100",
      payWay: String((checkoutStore as any).payMethod),
      channelName: (checkoutStore.currentPayInfo as any)?.payEnum || "-100",
    });

    const submitParams = getSubmitOrderParams();
    const res = await repayOrder(submitParams);
    console.log("handleSubmitOrder", res);

    setPayResult({
      id: (res as any)?.data?.txnSeqno || "",
      orderId: checkoutStore.repayOrderId || "",
    });

    switch (res.code) {
      case REPAY_RESULT_CODE.SUCCESS:
        return {
          success: true,
          data: res.data,
        };
      case REPAY_RESULT_CODE.RISK_ERROR:
        setRiskErrorOpen(true);
        setCommonError(res.msg);

        return {
          success: false,
          data: null,
        };
      default:
        UKToast.show({
          content: t("common.message.networkError"), // 弹窗内容
          icon: "error", // 可选：success, fail, loading, warn, info，影响图标
        });

        return {
          success: false,
          data: null,
        };
    }


  };

  // 提单参数
  const getSubmitOrderParams = () => {
    return {
      orderId: checkoutStore.repayOrderId || "",
      payMethod: checkoutStore.payMethod,
      extParam: {
        cancelUrl: CANCEL_URL,
        payEnum: (checkoutStore.currentPayInfo as any).payEnum,
        integrationType: (checkoutStore.currentPayInfo as any).integrationType,
        browserInfo: checkoutStore.isBnpCreditCardForm
          ? JSON.stringify(checkoutStore.browserInfo)
          : undefined,
      },
      sourceType: "4",
      rememberPayAccount: (checkoutStore as any)?.currentPayInfo.rememberInfo?.rememberCheckbox || false, // 是否记住支付账号
      payAccountSeqNo: (checkoutStore as any).currentPayAccount?.payAccountSeqNo || "", // 支付账号id
    };
  };

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const getIntervalResult = async (id: string, errorCb: () => void) => {
    let retry = 12;
    let result = "";
    while (retry--) {
      result = await getResult(id);
      if (result === RESULT_STR.SUCCESS) {
        props.onFinish();
        break;
      }
      if (result === RESULT_STR.ERROR) {
        errorCb();
        break;
      }
      await sleep(5000);
    }

    console.log(result, "res---");

    if (result !== RESULT_STR.SUCCESS) {
      errorCb();
    }
  };

  // 查询自支付结果
  const getResult = async (id: string) => {
    const requestData = {
      txnSeqno: id,
      txnScene: "Consumption",
    };

    const res = await getPayResult({ ...requestData });

    const statusRes = res?.status;
    if (statusRes) {
      // 支付状态是3
      if (Number(statusRes) === PAY_STATUS.SUCCESS) {
        return RESULT_STR.SUCCESS;
      }

      if (statusRes !== PAY_STATUS.INIT && statusRes !== PAY_STATUS.PROCESS) {
        return RESULT_STR.ERROR;
      }
    }
    return RESULT_STR.CONTINUE;
  };

  // 支付完成之后，重定向支付结果
  const redirectResult = async (requestData: any, onPayError: () => void) => {
    const res = await redirectPayResult({ ...requestData });
    return new Promise((resolve, reject) => {
      if (res?.status === PAY_STATUS.SUCCESS) {
        props.onFinish();
        return resolve(res);
      }
      if (res?.status !== PAY_STATUS.INIT && res?.status !== PAY_STATUS.PROCESS) {
        getIntervalResult(requestData.txnSeqno as string, onPayError);
        return resolve(res);
      }

      onPayError();
      return resolve(res);
    });
  };

  return (
    <div className={Styles.repayButtonBox}
      exptag={"exp|" + EXPOSURE_RECORD.Cashier_Pop_PayExpo}
      data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
      data-exptag-json-param={JSON.stringify({
        orderId: checkoutStore.repayOrderId || "-100",
        payWay: checkoutStore.payMethod ? String((checkoutStore as any).payMethod) : "-100",
        channelName: (checkoutStore.currentPayInfo as any)?.payEnum || "-100",
      })}
    >
      {!checkoutStore.payMethod && <Default />}
      {checkoutStore.payMethod === PAY_TYPE.CREDIT_CARD ? (
        <ReCard onSubmit={handleSubmitOrder} onFinish={props.onFinish} />
      ) : (
        ""
      )}
      <div
        style={{
          display: checkoutStore.payMethod === PAY_TYPE.PAYPAL ? "block" : "none",
          width: "100%",
        }}
      >
        <Repaypal
          onSubmit={handleSubmitOrder}
          onFinish={props.onFinish}
          onLoadingResult={redirectResult}
        />
      </div>
      {/* google pay */}
      <div
        style={{
          display: checkoutStore.payMethod === PAY_TYPE.GOOGLE_PAY ? "block" : "none",
        }}
      >
        <GooglePayButton
          onFinish={props.onFinish}
          onSubmit={handleSubmitOrder}
          onLoadingResult={redirectResult}
        />
      </div>
      {/* 分期 */}
      {checkoutStore.payMethod === PAY_TYPE.KLARNA && (
        <KlarnaPay
          onSubmit={handleSubmitOrder}
          onFinish={props.onFinish}
          onLoadingResult={redirectResult}
        />
      )}

      {/* wechat pay */}
      {checkoutStore.payMethod === PAY_TYPE.WECHAT_PAY && (
        <WechatPayButton
          onSubmit={handleSubmitOrder}
          onFinish={props.onFinish}
          onLoadingResult={redirectResult}
        />
      )}
      {/* Apple pay */}
      {checkoutStore.payMethod === PAY_TYPE.APPLE && (
        <ReApplePayButton
          onSubmit={handleSubmitOrder}
          onFinish={props.onFinish}
          onLoadingResult={redirectResult}
        />
      )}

      {/* 支付风控 */}

      {riskErrorOpen && <PayRiskModal
        open={riskErrorOpen}
        onClose={() => {
          props.onClose();
          setRiskErrorOpen(false);
        }}
        onOk={() => {
          props.onFresh();
          setRiskErrorOpen(false);
        }}
        content={commonError} />}
    </div>
  );
});

export default RepayButton;
